import type { PixelCanvas } from '../../../lib';
import { sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot } from './shared';

/**
 * The Arbalest's shots (the Knight set's gun, ADR-0160 amended): a crossbow bolt (a pale shaft, white fletching, a gold
 * head at the pellet's head) streaking a gold light behind it, the string's snap at the muzzle (a flash and a ring),
 * sparks off steel where it lands; and the flourish's armour (`knight_bit`: a gauntlet, a pauldron, a sword) and the
 * crowned helm tossed off (`knight_helm`).
 */

/** A bolt, its head's tip at (27, 8): a gold streak behind it flickering. */
const bolt = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const d = Math.abs(y - 8);
    // The gold head: a point.
    if (x > 21 && x < 28 && d < (28 - x) * 0.45) return d < 0.6 && x < 26 ? 'y' : y < 8 ? 'y' : 'Y';
    // The shaft.
    if (x > 8 && x <= 21 && d < 0.8) return y < 8 ? 't' : 'm';
    // The fletching: white vanes at its back.
    if (x > 6 && x <= 11 && d < 3 - (x - 7) * 0.4 && d >= 0.8) return y < 8 ? 'w' : '5';
    // The streak of light behind it, flickering.
    const len = 6 + (f % 2) * 2;
    if (x <= 8 && x > 8 - len && d < 1.2 - (8 - x) / (len * 1.2)) return x > 5 ? 'y' : 'Y';
    return null;
  });

/** The string's snap: a white flash, then a gold ring spreading and a few splinters flung forward. */
const snap = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const d = Math.hypot(x - 7, y - 20);
    if (f === 0) return d < 2.5 ? 'w' : d < 5 ? 'y' : d < 6.5 ? 'Y' : null;
    const r = f * 6;
    if (Math.abs(Math.hypot((x - 7) * 0.8, y - 20) - r) < 0.9 && x > 4) return f === 1 ? 'y' : 'Y';
    for (const [a, s] of [
      [-0.35, 1],
      [0.1, 1.3],
      [0.45, 0.9],
    ] as const) {
      const t = (f * 9 + 4) * s;
      if (Math.hypot(x - 7 - Math.cos(a) * t, y - 20 - Math.sin(a) * t) < 0.9) return f === 1 ? 'w' : '5';
    }
    return null;
  });

/** A bolt striking steel: a white star, then sparks thrown in a ring and a gold ring left ringing. */
const strike = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [dx, dy] = [x - 22, y - 22];
    const d = Math.hypot(dx, dy);
    if (f === 0) {
      if (Math.abs(dx) < 1 && Math.abs(dy) < 8) return 'w';
      if (Math.abs(dy) < 1 && Math.abs(dx) < 8) return 'w';
      return d < 4 ? 'y' : null;
    }
    const r = 3 + f * 4;
    if (f < 3 && Math.abs(d - r) < 0.8) return f === 1 ? 'y' : 'Y';
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2 + 0.3;
      const rr = 5 + f * (5 + (i % 3));
      if (Math.hypot(x - 22 - Math.cos(a) * rr, y - 22 - Math.sin(a) * rr + f * f * 0.6) < 0.85)
        return f === 3 ? 'U' : i % 2 ? 'w' : 'y';
    }
    return null;
  });

/** A gauntlet, a pauldron or a sword (16 × 16, middle (8, 8)), each a frame of its own. */
const armour = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    if (f === 0) {
      // A gauntlet: a steel cuff and fingers in plates.
      if (x > 3 && x < 9 && y > 5 && y < 13) return x < 5 ? '5' : x > 7 ? '3' : '4';
      if (x >= 9 && x < 14 && y > 6 && y < 12) return (Math.floor(x) - 9) % 2 ? '3' : y < 8 ? '5' : '4';
      return null;
    }
    if (f === 1) {
      // A pauldron: a dome of three lames.
      const d = Math.hypot((x - 8) / 6.5, (y - 10) / 5);
      if (d < 1 && y < 13) return Math.floor(y) % 3 === 0 ? '3' : x < 7 ? '5' : '4';
      return null;
    }
    // A sword: a steel blade, a gold crossguard, a brown grip and a gold pommel.
    const along = x + y - 16;
    const off = Math.abs(x - y) / Math.SQRT2;
    if (along > -6 && along < 12 && off < 0.9) return x > y ? '4' : 'w';
    if (along >= -8.5 && along <= -6 && off < 3.5) return 'Y';
    if (along > -12 && along < -8.5 && off < 0.9) return 'n';
    if (along >= -14 && along <= -12 && off < 1.4) return 'y';
    return null;
  });

/** The great helm tossed off (16 × 20, its foot at (8, 16)): steel, the gold cross and coronet. */
const crowned = () => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    if (y < 4 && y > 0.5 && [3, 8, 13].some((p) => Math.abs(x - p) < 2 - (4 - y) * 0.4)) return 'y';
    if (y >= 4 && y < 6 && x > 1.5 && x < 14.5) return x < 9 ? 'y' : 'Y';
    if (y >= 6 && y < 16 && x > 1.5 && x < 14.5) {
      if (x > 9 && x < 11.5) return 'Y';
      if (y > 9 && y < 11) return 'k';
      return x < 5 ? '5' : x > 12 ? '3' : '4';
    }
    return null;
  });

export default [
  sprite({
    name: 'bolt_shot',
    ...shot,
    notes: `The Arbalest's bullet (ADR-0160 amended): a crossbow bolt, its gold head at the pellet's head (lit along its
top), a pale walnut shaft, white fletching at its back, a short streak of gold light behind it flickering over 4 frames.
Turned along its flight. Rubric (4×): S4 R5 P4 L4 C4 A4 F4.`,
    anims: { idle: { fps: 14, frames: frames4(bolt) } },
  }),
  sprite({
    name: 'bolt_muzzle',
    ...muzzle,
    notes: `The Arbalest's muzzle flash (ADR-0160 amended): the string's snap, a white and gold flash, then a gold ring
spreading forward and three splinters flung out. Points right. Rubric (4×): S4 R4 P4 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(snap) } },
  }),
  sprite({
    name: 'bolt_burst',
    ...burst,
    notes: `The Arbalest's hit (ADR-0160 amended): a bolt striking steel, a white four-point star over a gold heart, then
a gold ring left ringing and sparks thrown out in a ring, falling and cooling to orange. 4 frames. Rubric (4×): S4 R4 P4
L4 C4 A4 F4.`,
    anims: { burst: { fps: 16, loop: false, frames: frames4(strike) } },
  }),
  sprite({
    name: 'knight_bit',
    group: 'fx',
    size: [16, 16],
    origin: [8, 8],
    density: 4,
    notes: `The Knight's knock-out armour (ADR-0160 amended), a frame each (the flourish picks one per piece): a steel
gauntlet, a pauldron of three lames, a sword (a steel blade, a gold crossguard and pommel, a brown grip). Rubric (4×): S4
R4 P4 L4 C4 F4.`,
    anims: { idle: { fps: 1, frames: frames3(armour) } },
  }),
  sprite({
    name: 'knight_helm',
    group: 'fx',
    size: [16, 20],
    origin: [8, 16],
    density: 4,
    notes: `The Knight's knock-out helm (ADR-0160 amended): the great helm tossed off, steel lit on its left, the gold
cross down its front over the dark eye slit, the gold coronet with three points on top. Rubric (4×): S4 R4 P4 L4 C4 F4.`,
    anims: { idle: { fps: 1, frames: [crowned()] } },
  }),
];
